import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Outlet } from "react-router-dom"
import { TabContent, TabPane } from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { Button, Flex } from "antd"
import { UserOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import Header from "components/accounting/Header"
import RangeSelector from "components/accounting/RangeSelector"
import Dashboard from "components/accounting/Dashboard"
import Details from "components/accounting/Details"
import DefaultErrorBoundary from "modules/errors/DefaultErrorBoundary"
import useLoaderWatchers from "modules/loader-watchers/hooks/useLoaderWatchers"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import { CustomSelect } from "ui/Input"

import useSearchFilter from "hooks/useSearchFilter"
import styles from "./Accounting.module.css"
import { useTitle } from "hooks/useTitle"
import { isEqual } from "lodash"

import { ACCESS_ATTRIBUTE, OVERVIEW_RANGE_OPTIONS } from "constants/accounting"
import { getCalendarCrew } from "store/staff"

export default function Accounting() {
  const { t } = useTranslation()
  useTitle(t("accounting.breadcrumb"))
  useLoaderWatchers({ watchers: ["accounting"] })
  const dispatch = useDispatch()

  const RANGE_MENU_OPTIONS = useMemo(
    () => Object.keys(OVERVIEW_RANGE_OPTIONS).map((value) => ({ value, label: t(`accounting.overview_range.${value}`) })),
    [t]
  )
  const [params, onChangeFilters] = useSearchFilter({ accessAttributes: ACCESS_ATTRIBUTE })
  const [paramsState, setParamsState] = useState(params)

  useEffect(() => {
    setParamsState(params)
  }, [params])

  const changeHandler = useCallback(
    ({ target }) =>
      setParamsState((prevParamsState) => ({
        ...prevParamsState,
        [target.name]: target.value
      })),
    []
  )

  useEffect(() => {
    if (isEqual(paramsState, params)) return
    onChangeFilters(paramsState)
  }, [paramsState, params, onChangeFilters])

  const isTipReport = params.type === "tip_report"

  useEffect(() => {
    if (isTipReport) dispatch(getCalendarCrew())
  }, [isTipReport, dispatch])

  const crewProfileItems = useSelector((state) => state.staff.calendarCrew || [])

  const crewOptions = useMemo(
    () => crewProfileItems.map((m) => ({ value: m.id, label: m.name })),
    [crewProfileItems]
  )

  const crewIdsParam = params?.["crew_ids[]"]
  let selectedCrewIds = []
  if (Array.isArray(crewIdsParam)) {
    selectedCrewIds = crewIdsParam
  } else if (crewIdsParam) {
    selectedCrewIds = [crewIdsParam]
  }
  const includeUnassigned = params?.include_unassigned ?? true

  const handleCrewChange = (values) => changeHandler({ target: { name: "crew_ids[]", value: values } })
  const handleIncludeUnassignedToggle = () => changeHandler({ target: { name: "include_unassigned", value: !includeUnassigned } })

  const tipToolbarSlot = isTipReport ? (
    <>
      <CustomSelect
        mode="multiple"
        placeholder={t("accounting.crew_filter.placeholder")}
        value={selectedCrewIds}
        onChange={handleCrewChange}
        options={crewOptions}
        allowClear
        className={styles.crewSelect}
        maxTagCount="responsive"
      />
      <Button
        color="primary"
        variant={includeUnassigned ? "solid" : "outlined"}
        type={includeUnassigned ? "primary" : "default"}
        icon={<UserOutlined />}
        onClick={handleIncludeUnassignedToggle}
      >
        {t("accounting.crew_filter.include_unassigned")}
      </Button>
    </>
  ) : null

  const activeTab = params.type === "business_overview" ? "dashboard" : "details"
  const isBusinessOverview = params.type === "business_overview"

  return (
    <div className="flex-fill z-0 position-relative px-20 py-20" id="accounting">
      <Header params={params} onChangeFilters={changeHandler} />
      <Flex align="center" gap={8} justify="end" wrap className="my-m">
        {isBusinessOverview && (
          <CustomSelect
            value={params.overview_range || "current_month"}
            onChange={(value) => changeHandler({ target: { name: "overview_range", value } })}
            options={RANGE_MENU_OPTIONS}
          />
        )}
        <RangeSelector params={params} onChangeFilters={changeHandler} />
      </Flex>
      <FirstLoading name="accounting">
        <TabContent activeTab={activeTab} className="w-100">
          <TabPane tabId="dashboard">
            <Dashboard params={params} onChangeFilters={changeHandler} />
          </TabPane>
          <TabPane tabId="details">
            <Details params={params} onChangeFilters={changeHandler} tipToolbarSlot={tipToolbarSlot} />
          </TabPane>
        </TabContent>

        <DefaultErrorBoundary>
          <Outlet />
        </DefaultErrorBoundary>
      </FirstLoading>
    </div>
  )
}
